import { ReactNode } from "react";
import { CERT_RAW } from "../../constants/certificate";
import { Certification, CertificationData } from "../../types/certificate";
import { FormState } from "../../components/pages/certificates/types";

export function generateCerts(): Certification[] {
    return CERT_RAW.map((r, i) => ({
        id: `cert-${String(i + 1).padStart(3, "0")}`,
        title: r.title,
        description: `This certification confirms Aurel Global's compliance with ${r.title} standards, validated by ${r.issuedBy}. It demonstrates our ongoing commitment to quality, safety, and international regulatory compliance across all product lines.`,
        imageUrl: "",
        issuedBy: r.issuedBy,
        issuedDate: r.issued,
        expiryDate: r.expiry,
        status: r.status,
        createdAt: new Date(2024, i % 12, (i * 3 % 28) + 1).toISOString(),
        updatedAt: new Date(2025, (i * 2) % 12, (i * 5 % 28) + 1).toISOString(),
    }));
}

export function fmt(iso: string) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function isExpiringSoon(expiry: string): boolean {
    if (!expiry) return false;
    const diff = new Date(expiry).getTime() - Date.now();
    return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000; // 90 days
}

export function highlight(text: string, q: string): ReactNode {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>
                {text.slice(idx, idx + q.length)}
            </mark>
            {text.slice(idx + q.length)}
        </>
    );
}

export function StatusBadge({ status, expiry }: { status: Certification["status"]; expiry?: string }) {
    const soon = expiry ? isExpiringSoon(expiry) : false;
    const map = {
        active: { bg: "rgba(109,220,109,0.10)", border: "rgba(109,220,109,0.25)", color: "#6ddc6d", label: "Active" },
        expired: { bg: "rgba(224,82,82,0.10)", border: "rgba(224,82,82,0.25)", color: "#e05252", label: "Expired" },
        pending: { bg: "rgba(245,166,35,0.10)", border: "rgba(245,166,35,0.25)", color: "#f5a623", label: "Pending" },
    };
    const s = soon ? { bg: "rgba(245,166,35,0.10)", border: "rgba(245,166,35,0.25)", color: "#f5a623", label: "Expiring soon" } : map[status];
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.4px",
            background: s.bg, border: `1px solid ${s.border}`, color: s.color,
            borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap",
        }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
            {s.label}
        </span>
    );
}

export function CertPlaceholder({ size = 40 }: { size?: number }) {
    return (
        <div style={{
            width: size, height: size, borderRadius: 6,
            background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
            <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="none"
                stroke="var(--text3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4" /><path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                <polyline points="16 3 17.5 4.5 21 2" />
            </svg>
        </div>
    );
}



// ─── Manage Certifications ──────────────────────────────────────────────────────────────────

export function hydrateForm(cert: CertificationData): FormState {
    return { ...cert, imageFile: null, imagePreview: cert.imageUrl };
}